import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { winApi, skillApi } from '@/services/skillKnowledgeApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Edit, Trash2 } from 'lucide-react';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import type { Win, Skill } from '@/types/skillKnowledge';

export const WinDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAdminAuth();
  const [skills, setSkills] = useState<Map<string, Skill>>(new Map());

  const canEdit = hasPermission('edit', 'skill');
  const canDelete = hasPermission('delete', 'skill');

  // Use entity hook for fetching win
  const {
    data: win,
    isLoading,
    error,
    refetch,
  } = useEntity<Win>({
    fetchFn: (winId: string) => winApi.getById(winId),
    entityId: id,
    entityName: 'win',
    enabled: !!id,
  });

  // Fetch skill details for required skills
  useEffect(() => {
    if (!win?.requiredSkills || win.requiredSkills.length === 0) return;

    Promise.allSettled(
      win.requiredSkills.map((reqSkill) => skillApi.getById(reqSkill.skillId))
    )
      .then((results) => {
        const skillMap = new Map<string, Skill>();
        results.forEach((result) => {
          if (result.status === 'fulfilled' && result.value) {
            const skill = result.value;
            if (skill.id) {
              skillMap.set(skill.id, skill);
            }
          }
        });
        setSkills(skillMap);
      })
      .catch((error) => {
        console.error('Error loading skills:', error);
      });
  }, [win]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Win ID is required');
      await winApi.delete(id);
    },
    entityName: 'win',
    operation: 'delete',
    onSuccess: () => {
      window.location.href = '/admin/wins';
    },
  });

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this win?')) return;
    await deleteMutation.mutate();
  };

  if (isLoading) {
    return <LoadingState message="Loading win..." />;
  }

  if (error || !win) {
    return <ErrorState error={error || new Error('Win not found')} onRetry={refetch} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/wins">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{win.name}</h1>
            <p className="text-muted-foreground mt-2">Win Details</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <Button asChild>
              <Link to={`/admin/wins/${id}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          )}
          {canDelete && (
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isLoading}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <CopyableId id={win.id || id || ''} label="Win ID" />
            <div>
              <label className="text-sm font-medium text-muted-foreground">Name</label>
              <p className="mt-1">{win.name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Description</label>
              <p className="mt-1">{win.description}</p>
            </div>
            {win.summary && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Summary</label>
                <p className="mt-1">{win.summary}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-muted-foreground">Active</label>
              <p className="mt-1">
                <span
                  className={`px-2 py-1 rounded-full text-xs ${
                    win.isActive
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                  }`}
                >
                  {win.isActive ? 'Active' : 'Inactive'}
                </span>
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Audit Information</CardTitle>
            <CardDescription>Creation and update history</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Created At</label>
                <p className="mt-1 text-sm">
                  {win.createdAt ? new Date(win.createdAt).toLocaleString() : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Updated At</label>
                <p className="mt-1 text-sm">
                  {win.updatedAt ? new Date(win.updatedAt).toLocaleString() : 'N/A'}
                </p>
              </div>
              {win.createdBy && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Created By</label>
                  <p className="mt-1 text-sm">{win.createdBy}</p>
                </div>
              )}
              {win.updatedBy && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Updated By</label>
                  <p className="mt-1 text-sm">{win.updatedBy}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Required Skills */}
      {win.requiredSkills && win.requiredSkills.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Required Skills ({win.requiredSkills.length})</CardTitle>
            <CardDescription>Skills needed for this win</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {win.requiredSkills.map((reqSkill, idx) => {
                const skillDetails = skills.get(reqSkill.skillId);
                return (
                  <div key={idx} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <Link
                        to={`/admin/skills/${reqSkill.skillId}`}
                        className="font-medium text-blue-600 hover:underline"
                      >
                        {skillDetails?.name || 'View Skill'}
                      </Link>
                    </div>
                    <CopyableId id={reqSkill.skillId} label="Skill ID" />
                    {skillDetails?.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {skillDetails.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded text-xs">
                        {reqSkill.proficiencyLevel}
                      </span>
                      {skillDetails?.category && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-xs">
                          {skillDetails.category.replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Related Pain Points */}
      {win.relatedPainPointIds && win.relatedPainPointIds.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Related Pain Points ({win.relatedPainPointIds.length})</CardTitle>
            <CardDescription>Pain points associated with this win</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {win.relatedPainPointIds.map((painPointId) => (
                <div key={painPointId} className="border rounded-lg p-4 space-y-2">
                  <Link
                    to={`/admin/pain-points/${painPointId}`}
                    className="font-medium text-blue-600 hover:underline"
                  >
                    View Pain Point
                  </Link>
                  <CopyableId id={painPointId} label="Pain Point ID" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WinDetailPage;






